import * as React from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { format, startOfMonth, subMonths } from 'date-fns';
import useAuthorize from '../hooks/useAuthorize';
import { DASHBOARD_MODULE_ROLES } from '../roles/portalRoles';
import DashboardPanelHeader from '../components/DashboardPanelHeader';
import { AnalyticsFreshnessNote } from '../utils/AnalyticsFreshnessNote';
import {
  BusinessCohortActivityRow,
  CohortRetentionRow,
  CohortRevenueRow,
  fetchInvestorCohorts,
  InquiryRetentionRow,
  InvestorCohortResponse,
  InvestorSegment,
} from './investorCohortApi';
import { CohortRevenueChart, OperatingTrendChart } from './InvestorCohortCharts';

const inr = (value: number) => `₹${Number(value || 0).toLocaleString('en-IN', {
  maximumFractionDigits: 0,
})}`;
const count = (value: number) => Number(value || 0).toLocaleString('en-IN');
const pct = (value: number | null | undefined) => (
  value == null ? '—' : `${Number(value).toFixed(1)}%`
);
const deltaLabel = (delta: number | null | undefined) => {
  if (delta == null || Math.abs(delta) < 0.05) return 'avg';
  return `${delta > 0 ? '+' : ''}${delta.toFixed(1)} pp`;
};
const deltaColor = (delta: number | null | undefined) => {
  if (delta == null || Math.abs(delta) < 2) return 'text.secondary';
  return delta > 0 ? 'success.main' : 'error.main';
};

const RETENTION_HORIZON_OPTIONS = [
  { maxMonthOffset: 6, label: 'M0 to M6' },
  { maxMonthOffset: 12, label: 'M0 to M12' },
  { maxMonthOffset: 23, label: 'M0 to M23' },
] as const;

const MetricCard = ({
  label,
  value,
  note,
}: {
  label: string;
  value: string;
  note?: string;
}) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h5" sx={{ mt: 0.5 }}>{value}</Typography>
      {note && <Typography variant="caption" color="text.secondary">{note}</Typography>}
    </CardContent>
  </Card>
);

type OffsetBenchmark = {
  active: number;
  customers: number;
  pct: number | null;
};

const buildOffsetBenchmarks = <T extends { month_offset: number; cohort_size: number; is_observable: boolean }>(
  rows: T[],
  activeSelector: (row: T) => number
) => {
  const byOffset = new Map<number, { active: number; customers: number }>();
  rows.forEach((row) => {
    if (!row.is_observable) return;
    const current = byOffset.get(row.month_offset) || { active: 0, customers: 0 };
    current.active += Number(activeSelector(row) || 0);
    current.customers += Number(row.cohort_size || 0);
    byOffset.set(row.month_offset, current);
  });
  return new Map<number, OffsetBenchmark>(
    Array.from(byOffset.entries()).map(([offset, value]) => [
      offset,
      {
        ...value,
        pct: value.customers ? Number(((value.active / value.customers) * 100).toFixed(2)) : null,
      },
    ])
  );
};

const HeatmapValue = ({
  value,
  benchmark,
}: {
  value: number;
  benchmark?: OffsetBenchmark;
}) => {
  const delta = benchmark?.pct == null ? null : value - benchmark.pct;
  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>{pct(value)}</Typography>
      <Typography variant="caption" color={deltaColor(delta)}>{deltaLabel(delta)}</Typography>
    </Box>
  );
};

const RetentionHeatmap = ({
  rows,
  maxOffset,
}: {
  rows: CohortRetentionRow[];
  maxOffset: number;
}) => {
  const cohorts = Array.from(new Set(rows.map((row) => row.cohort_month)));
  const byCell = new Map(rows.map((row) => [
    `${row.cohort_month}-${row.month_offset}`,
    row,
  ]));
  const benchmarks = buildOffsetBenchmarks(rows, (row) => row.retained_customers);

  if (!cohorts.length) {
    return <Alert severity="info">No registration cohorts exist in this date range.</Alert>;
  }

  return (
    <TableContainer sx={{ maxHeight: 560 }}>
      <Table size="small" stickyHeader aria-label="Registration cohort order retention">
        <TableHead>
          <TableRow>
            <TableCell>Cohort</TableCell>
            <TableCell align="right">Size</TableCell>
            {Array.from({ length: maxOffset + 1 }, (_, index) => (
              <TableCell key={index} align="center">M{index}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell component="th" scope="row" sx={{ whiteSpace: 'nowrap', fontWeight: 700 }}>
              Portfolio avg
            </TableCell>
            <TableCell align="right">—</TableCell>
            {Array.from({ length: maxOffset + 1 }, (_, offset) => {
              const benchmark = benchmarks.get(offset);
              return (
                <TableCell key={offset} align="center" sx={{ fontWeight: 700 }}>
                  {benchmark?.pct == null ? '—' : pct(benchmark.pct)}
                </TableCell>
              );
            })}
          </TableRow>
          {cohorts.map((cohort) => {
            const size = byCell.get(`${cohort}-0`)?.cohort_size || 0;
            return (
              <TableRow key={cohort}>
                <TableCell component="th" scope="row" sx={{ whiteSpace: 'nowrap' }}>
                  {cohort}{size < 20 ? ' *' : ''}
                </TableCell>
                <TableCell align="right">{count(size)}</TableCell>
                {Array.from({ length: maxOffset + 1 }, (_, offset) => {
                  const cell = byCell.get(`${cohort}-${offset}`);
                  if (!cell?.is_observable) {
                    return <TableCell key={offset} align="center" sx={{ color: 'text.disabled' }}>—</TableCell>;
                  }
                  const intensity = Math.min(0.9, 0.08 + (cell.retention_pct / 100) * 0.82);
                  const benchmark = benchmarks.get(offset);
                  return (
                    <Tooltip
                      key={offset}
                      title={`${count(cell.retained_customers)} of ${count(cell.cohort_size)} customers; portfolio avg ${benchmark?.pct == null ? '—' : pct(benchmark.pct)}`}
                    >
                      <TableCell
                        align="center"
                        sx={{
                          bgcolor: `rgba(25, 118, 210, ${intensity})`,
                          color: intensity > 0.5 ? 'common.white' : 'text.primary',
                          fontWeight: 600,
                        }}
                      >
                        <HeatmapValue value={cell.retention_pct} benchmark={benchmark} />
                      </TableCell>
                    </Tooltip>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const SegmentTable = ({ rows }: { rows: InvestorSegment[] }) => (
  <TableContainer sx={{ maxHeight: 420 }}>
    <Table size="small" stickyHeader>
      <TableHead>
        <TableRow>
          <TableCell>Segment</TableCell>
          <TableCell align="right">Customers</TableCell>
          <TableCell align="right">Paying</TableCell>
          <TableCell align="right">Repeat</TableCell>
          <TableCell align="right">Repeat rate</TableCell>
          <TableCell align="right">GMV</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.segment}>
            <TableCell>{row.segment}</TableCell>
            <TableCell align="right">{row.customers == null ? '—' : count(row.customers)}</TableCell>
            <TableCell align="right">{count(row.paying_customers)}</TableCell>
            <TableCell align="right">
              {row.repeat_paying_buyers == null ? '—' : count(row.repeat_paying_buyers)}
            </TableCell>
            <TableCell align="right">
              {row.repeat_buyer_rate_pct == null ? '—' : `${row.repeat_buyer_rate_pct.toFixed(1)}%`}
            </TableCell>
            <TableCell align="right">{inr(row.gmv_inr)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const InquiryRetentionHeatmap = ({
  rows,
  maxOffset,
}: {
  rows: InquiryRetentionRow[];
  maxOffset: number;
}) => {
  const cohorts = Array.from(new Set(rows.map((row) => row.cohort_month)));
  const byCell = new Map(rows.map((row) => [
    `${row.cohort_month}-${row.month_offset}`,
    row,
  ]));
  const benchmarks = buildOffsetBenchmarks(rows, (row) => row.inquiring_customers);
  return (
    <TableContainer sx={{ maxHeight: 480 }}>
      <Table size="small" stickyHeader aria-label="Registration cohort inquiry retention">
        <TableHead>
          <TableRow>
            <TableCell>Cohort</TableCell>
            <TableCell align="right">Size</TableCell>
            {Array.from({ length: maxOffset + 1 }, (_, index) => (
              <TableCell key={index} align="center">M{index}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 700 }}>Portfolio avg</TableCell>
            <TableCell align="right">—</TableCell>
            {Array.from({ length: maxOffset + 1 }, (_, offset) => {
              const benchmark = benchmarks.get(offset);
              return (
                <TableCell key={offset} align="center" sx={{ fontWeight: 700 }}>
                  {benchmark?.pct == null ? '—' : pct(benchmark.pct)}
                </TableCell>
              );
            })}
          </TableRow>
          {cohorts.map((cohort) => (
            <TableRow key={cohort}>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{cohort}</TableCell>
              <TableCell align="right">{count(byCell.get(`${cohort}-0`)?.cohort_size || 0)}</TableCell>
              {Array.from({ length: maxOffset + 1 }, (_, offset) => {
                const cell = byCell.get(`${cohort}-${offset}`);
                const benchmark = benchmarks.get(offset);
                return (
                  <TableCell key={offset} align="center" sx={{ color: cell?.is_observable ? 'text.primary' : 'text.disabled' }}>
                    {cell?.is_observable
                      ? <HeatmapValue value={cell.inquiry_retention_pct} benchmark={benchmark} />
                      : '—'}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const BusinessActivityHeatmap = ({
  rows,
  maxOffset,
  ariaLabel,
}: {
  rows: BusinessCohortActivityRow[];
  maxOffset: number;
  ariaLabel: string;
}) => {
  const cohorts = Array.from(new Set(rows.map((row) => row.cohort_month)));
  const byCell = new Map(rows.map((row) => [
    `${row.cohort_month}-${row.month_offset}`,
    row,
  ]));
  const benchmarks = buildOffsetBenchmarks(rows, (row) => row.active_customers);

  if (!cohorts.length) {
    return <Alert severity="info">No cohorts exist in this date range.</Alert>;
  }

  return (
    <TableContainer sx={{ maxHeight: 480 }}>
      <Table size="small" stickyHeader aria-label={ariaLabel}>
        <TableHead>
          <TableRow>
            <TableCell>Cohort</TableCell>
            <TableCell align="right">Size</TableCell>
            {Array.from({ length: maxOffset + 1 }, (_, index) => (
              <TableCell key={index} align="center">M{index}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 700 }}>Portfolio avg</TableCell>
            <TableCell align="right">—</TableCell>
            {Array.from({ length: maxOffset + 1 }, (_, offset) => {
              const benchmark = benchmarks.get(offset);
              return (
                <TableCell key={offset} align="center" sx={{ fontWeight: 700 }}>
                  {benchmark?.pct == null ? '—' : pct(benchmark.pct)}
                </TableCell>
              );
            })}
          </TableRow>
          {cohorts.map((cohort) => (
            <TableRow key={cohort}>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{cohort}</TableCell>
              <TableCell align="right">{count(byCell.get(`${cohort}-0`)?.cohort_size || 0)}</TableCell>
              {Array.from({ length: maxOffset + 1 }, (_, offset) => {
                const cell = byCell.get(`${cohort}-${offset}`);
                const benchmark = benchmarks.get(offset);
                return (
                  <TableCell key={offset} align="center" sx={{ color: cell?.is_observable ? 'text.primary' : 'text.disabled' }}>
                    {cell?.is_observable
                      ? <HeatmapValue value={cell.activity_pct} benchmark={benchmark} />
                      : '—'}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const latestRevenueByCohort = (revenue: CohortRevenueRow[]) => {
  const byCohort = new Map<string, number>();
  revenue.forEach((row) => {
    byCohort.set(
      row.cohort_month,
      Math.max(byCohort.get(row.cohort_month) || 0, row.cumulative_gmv_inr)
    );
  });
  return byCohort;
};

const cellByCohortOffset = <T extends { cohort_month: string; month_offset: number }>(rows: T[]) => (
  new Map(rows.map((row) => [`${row.cohort_month}-${row.month_offset}`, row]))
);

type CohortQualityRow = {
  cohort: string;
  size: number;
  m0Inquiry?: InquiryRetentionRow;
  m0Order?: CohortRetentionRow;
  m1Order?: CohortRetentionRow;
  m3Order?: CohortRetentionRow;
  cumulativeGmv: number;
  gmvPerSignup: number;
  status: string;
};

const buildCohortQualityRows = (
  retention: CohortRetentionRow[],
  inquiryRetention: InquiryRetentionRow[],
  revenue: CohortRevenueRow[]
): CohortQualityRow[] => {
  const cohorts = Array.from(new Set(retention.map((row) => row.cohort_month)));
  const orderByCell = cellByCohortOffset(retention);
  const inquiryByCell = cellByCohortOffset(inquiryRetention);
  const revenueByCohort = latestRevenueByCohort(revenue);
  const orderBenchmarks = buildOffsetBenchmarks(retention, (row) => row.retained_customers);
  const inquiryBenchmarks = buildOffsetBenchmarks(inquiryRetention, (row) => row.inquiring_customers);
  const gmvPerSignupValues = cohorts
    .map((cohort) => {
      const size = orderByCell.get(`${cohort}-0`)?.cohort_size || 0;
      return size ? (revenueByCohort.get(cohort) || 0) / size : 0;
    })
    .filter((value) => value > 0)
    .sort((a, b) => a - b);
  const medianGmvPerSignup = gmvPerSignupValues.length
    ? gmvPerSignupValues[Math.floor(gmvPerSignupValues.length / 2)]
    : 0;

  return cohorts.map((cohort) => {
    const size = orderByCell.get(`${cohort}-0`)?.cohort_size || 0;
    const m0Inquiry = inquiryByCell.get(`${cohort}-0`) as InquiryRetentionRow | undefined;
    const m0Order = orderByCell.get(`${cohort}-0`) as CohortRetentionRow | undefined;
    const m1Order = orderByCell.get(`${cohort}-1`) as CohortRetentionRow | undefined;
    const m3Order = orderByCell.get(`${cohort}-3`) as CohortRetentionRow | undefined;
    const cumulativeGmv = revenueByCohort.get(cohort) || 0;
    const gmvPerSignup = size ? cumulativeGmv / size : 0;
    const score = [
      m0Inquiry?.is_observable && inquiryBenchmarks.get(0)?.pct != null
        ? m0Inquiry.inquiry_retention_pct - Number(inquiryBenchmarks.get(0)?.pct) >= 5
        : false,
      m0Order?.is_observable && orderBenchmarks.get(0)?.pct != null
        ? m0Order.retention_pct - Number(orderBenchmarks.get(0)?.pct) >= 2
        : false,
      m1Order?.is_observable && orderBenchmarks.get(1)?.pct != null
        ? m1Order.retention_pct - Number(orderBenchmarks.get(1)?.pct) >= 2
        : false,
      m3Order?.is_observable && orderBenchmarks.get(3)?.pct != null
        ? m3Order.retention_pct - Number(orderBenchmarks.get(3)?.pct) >= 2
        : false,
      medianGmvPerSignup > 0 && gmvPerSignup >= medianGmvPerSignup,
    ].filter(Boolean).length;
    const status = size < 20
      ? 'Directional'
      : score >= 3
        ? 'Better than avg'
        : score <= 1
          ? 'Needs attention'
          : 'Normal';
    return {
      cohort,
      size,
      m0Inquiry,
      m0Order,
      m1Order,
      m3Order,
      cumulativeGmv,
      gmvPerSignup,
      status,
    };
  });
};

const CohortQualityScorecard = ({
  retention,
  inquiryRetention,
  revenue,
}: {
  retention: CohortRetentionRow[];
  inquiryRetention: InquiryRetentionRow[];
  revenue: CohortRevenueRow[];
}) => {
  const rows = buildCohortQualityRows(retention, inquiryRetention, revenue);

  return (
    <TableContainer sx={{ maxHeight: 520 }}>
      <Table size="small" stickyHeader aria-label="Cohort quality scorecard">
        <TableHead>
          <TableRow>
            <TableCell>Cohort</TableCell>
            <TableCell align="right">Size</TableCell>
            <TableCell align="right">M0 inquiry</TableCell>
            <TableCell align="right">M0 order</TableCell>
            <TableCell align="right">M1 order</TableCell>
            <TableCell align="right">M3 order</TableCell>
            <TableCell align="right">Cumulative GMV</TableCell>
            <TableCell align="right">GMV / signup</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.cohort}>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.cohort}</TableCell>
              <TableCell align="right">{count(row.size)}</TableCell>
              <TableCell align="right">{row.m0Inquiry?.is_observable ? pct(row.m0Inquiry.inquiry_retention_pct) : '—'}</TableCell>
              <TableCell align="right">{row.m0Order?.is_observable ? pct(row.m0Order.retention_pct) : '—'}</TableCell>
              <TableCell align="right">{row.m1Order?.is_observable ? pct(row.m1Order.retention_pct) : '—'}</TableCell>
              <TableCell align="right">{row.m3Order?.is_observable ? pct(row.m3Order.retention_pct) : '—'}</TableCell>
              <TableCell align="right">{inr(row.cumulativeGmv)}</TableCell>
              <TableCell align="right">{inr(row.gmvPerSignup)}</TableCell>
              <TableCell>{row.status}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const DashboardInvestorCohorts = () => {
  useAuthorize(DASHBOARD_MODULE_ROLES);
  const today = new Date();
  const [fromDate, setFromDate] = React.useState(
    format(startOfMonth(subMonths(today, 23)), 'yyyy-MM-dd')
  );
  const [toDate, setToDate] = React.useState(format(today, 'yyyy-MM-dd'));
  const [maxMonthOffset, setMaxMonthOffset] = React.useState(12);
  const [segmentDimension, setSegmentDimension] = React.useState<
    keyof InvestorCohortResponse['segments']
  >('buyer_state');
  const [data, setData] = React.useState<InvestorCohortResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    fetchInvestorCohorts({
      from_date: fromDate,
      to_date: toDate,
      max_month_offset: maxMonthOffset,
    })
      .then((payload) => {
        if (active) setData(payload);
      })
      .catch((requestError) => {
        if (active) {
          const responseData = requestError?.response?.data;
          const responseMessage = typeof responseData === 'string'
            ? responseData
            : responseData?.message;
          setError(responseMessage || 'Unable to load cohorts and segmentation data');
          setData(null);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [fromDate, toDate, maxMonthOffset]);

  const exportParams = React.useMemo(() => ({
    from_date: fromDate,
    to_date: toDate,
    period: String(maxMonthOffset),
  }), [fromDate, toDate, maxMonthOffset]);
  const cohortQualityExportRows = React.useMemo(() => (
    data
      ? buildCohortQualityRows(data.retention, data.inquiry_retention, data.revenue).map((row) => ({
        cohort: row.cohort,
        size: row.size,
        m0_inquiry_pct: row.m0Inquiry?.is_observable ? row.m0Inquiry.inquiry_retention_pct : null,
        m0_order_pct: row.m0Order?.is_observable ? row.m0Order.retention_pct : null,
        m1_order_pct: row.m1Order?.is_observable ? row.m1Order.retention_pct : null,
        m3_order_pct: row.m3Order?.is_observable ? row.m3Order.retention_pct : null,
        cumulative_gmv_inr: row.cumulativeGmv,
        gmv_per_signup_inr: row.gmvPerSignup,
        status: row.status,
      }))
      : []
  ), [data]);

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4">Cohorts & Segmentation</Typography>
        <Typography color="text.secondary">
          Registration cohorts, repeat paying buyers, and segment views. Order-based metrics, not subscriptions.
        </Typography>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              fullWidth
              label="Cohorts from"
              type="date"
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              fullWidth
              label="Data through"
              type="date"
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth>
              <InputLabel>Cohort table width</InputLabel>
              <Select
                value={maxMonthOffset}
                label="Cohort table width"
                onChange={(event) => setMaxMonthOffset(Number(event.target.value))}
              >
                {RETENTION_HORIZON_OPTIONS.map((option) => (
                  <MenuItem key={option.maxMonthOffset} value={option.maxMonthOffset}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Controls registration and inquiry cohort heatmaps only (M0 = signup month).
              </Typography>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && data && (
        <>
          {data.data_through && (
            <AnalyticsFreshnessNote
              dataThrough={data.data_through}
              lastUpdatedAt={data.last_updated_at}
            />
          )}
          <Card sx={{ mb: 2 }}>
            <DashboardPanelHeader
              title="Monthly operating trend"
              subheader={
                data.summary.is_partial_month
                  ? 'Customer growth, paying buyers, repeat buyers and GMV. The latest month is month-to-date.'
                  : 'Customer growth, paying buyers, repeat buyers and GMV across your selected date range'
              }
              exportFilename="credco-cohort-monthly-mis.xlsx"
              exportPanel="cohort_monthly_mis"
              exportParams={exportParams}
            />
            <CardContent><OperatingTrendChart monthly={data.monthly} /></CardContent>
          </Card>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            {data.retention_milestones.map((milestone) => (
              <Grid item xs={6} md={3} key={milestone.month_offset}>
                <MetricCard
                  label={`M${milestone.month_offset} order retention`}
                  value={milestone.retention_pct == null ? 'Not observable' : `${milestone.retention_pct.toFixed(1)}%`}
                  note={`${count(milestone.cohort_customers)} customers across ${milestone.cohorts_included} cohorts`}
                />
              </Grid>
            ))}
          </Grid>

          <Card sx={{ mb: 2 }}>
            <DashboardPanelHeader
              title="Cohort quality scorecard"
              subheader="One row per signup month: early inquiry and order rates (M0/M1/M3), total GMV, and GMV per signup. Status flags cohorts that beat the portfolio on those same month offsets—not just bigger or older months."
              exportFilename="credco-cohort-quality-scorecard.xlsx"
              summaryRows={cohortQualityExportRows}
            />
            <CardContent>
              <CohortQualityScorecard
                retention={data.retention}
                inquiryRetention={data.inquiry_retention}
                revenue={data.revenue}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Use this table to rank signup months: compare M1/M3 order % and GMV/signup to spot quality,
                not only total GMV. Fewer than 20 signups in a month stays &quot;Directional&quot;.
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ mb: 2 }}>
            <DashboardPanelHeader
              title="Registration cohort order retention"
              subheader="Each row is a signup month; M0/M1/… is the % of that cohort with at least one valid order in that calendar month (not cumulative). Portfolio avg row and green/red deltas show how each month compares at the same age."
              exportFilename="credco-order-retention-cohorts.xlsx"
              exportPanel="cohort_retention"
              exportParams={exportParams}
            />
            <CardContent>
              <RetentionHeatmap rows={data.retention} maxOffset={data.filters.max_month_offset} />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                * Cohorts below 20 customers are directional. A dash means that month is not yet observable.
                &quot;+X pp&quot; = percentage points above portfolio average for that column; cells do not sum to 100% across a row.
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ mb: 2 }}>
            <DashboardPanelHeader
              title="Cohort monetization"
              subheader="Total valid-order GMV from everyone who registered in each month (through your selected end date). Taller bars mean more revenue from that vintage—check GMV/signup in the scorecard when cohort sizes differ."
              exportFilename="credco-cohort-revenue.xlsx"
              exportPanel="cohort_revenue"
              exportParams={exportParams}
            />
            <CardContent><CohortRevenueChart revenue={data.revenue} /></CardContent>
          </Card>

          <Card sx={{ mb: 2 }}>
            <DashboardPanelHeader
              title="Pre-revenue inquiry retention"
              subheader="Same signup-month rows as order retention, but activity is inquiries (pre-revenue intent). Compare each cell to the portfolio avg to see which signup months engage buyers before they order."
              exportFilename="credco-inquiry-retention-cohorts.xlsx"
              exportPanel="cohort_inquiry_retention"
              exportParams={exportParams}
            />
            <CardContent>
              <InquiryRetentionHeatmap
                rows={data.inquiry_retention}
                maxOffset={data.filters.max_month_offset}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                A customer can appear in multiple month columns if they inquired in more than one month.
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ mb: 2 }}>
            <DashboardPanelHeader
              title="First inquiry cohort conversion"
              subheader="Rows are grouped by the month of a customer's first inquiry—not signup. Each cell is the % of that inquiry cohort who placed a valid order in that later month (inquiry-to-order funnel by vintage)."
              exportFilename="credco-first-inquiry-order-cohorts.xlsx"
              summaryRows={data.first_inquiry_order_retention}
            />
            <CardContent>
              <BusinessActivityHeatmap
                rows={data.first_inquiry_order_retention}
                maxOffset={data.filters.max_month_offset}
                ariaLabel="First inquiry cohort order conversion"
              />
            </CardContent>
          </Card>

          <Card sx={{ mb: 2 }}>
            <DashboardPanelHeader
              title="First order cohort repeat"
              subheader="Rows are grouped by first valid order month. Each cell is the % of those first-time buyers who ordered again (or still ordered) in that calendar month—use M1+ to judge repeat revenue quality."
              exportFilename="credco-first-order-repeat-cohorts.xlsx"
              summaryRows={data.first_order_repeat_retention}
            />
            <CardContent>
              <BusinessActivityHeatmap
                rows={data.first_order_repeat_retention}
                maxOffset={data.filters.max_month_offset}
                ariaLabel="First order cohort repeat retention"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                M0 is the first-order month, so it is the buyer cohort baseline. M1+ is the useful repeat signal.
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ mb: 2 }}>
            <DashboardPanelHeader
              title="Customer segmentation"
              subheader="Unknown is retained when profiles or attribution are incomplete"
              exportFilename={`credco-cohort-segments-${segmentDimension}.xlsx`}
              exportPanel="cohort_segments"
              exportParams={{ ...exportParams, dimension: segmentDimension }}
              action={(
                <FormControl size="small" sx={{ minWidth: 180, mr: 1 }}>
                  <InputLabel>Dimension</InputLabel>
                  <Select
                    value={segmentDimension}
                    label="Dimension"
                    onChange={(event) => setSegmentDimension(
                      event.target.value as keyof InvestorCohortResponse['segments']
                    )}
                  >
                    <MenuItem value="buyer_state">Buyer state</MenuItem>
                    <MenuItem value="business_type">Business type</MenuItem>
                    <MenuItem value="product_category">Product category</MenuItem>
                    <MenuItem value="acquisition_source">Acquisition source</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
            <CardContent>
              {data.segments[segmentDimension].length
                ? <SegmentTable rows={data.segments[segmentDimension]} />
                : <Alert severity="info">This segment is unavailable for the selected period.</Alert>}
            </CardContent>
          </Card>

          <Card>
            <DashboardPanelHeader
              title="Pre-revenue engagement"
              subheader="First-party audit engagement, shown separately from paying-user metrics"
              exportFilename="credco-cohort-engagement.xlsx"
              exportPanel="cohort_engagement"
              exportParams={exportParams}
            />
            <CardContent>
              {!data.engagement.available ? (
                <Alert severity="warning">Engagement rollups are currently unavailable.</Alert>
              ) : (
                <>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={4}>
                      <MetricCard label="Active in last 30 days" value={count(data.engagement.dormancy.active_30d)} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <MetricCard label="Dormant 90+ days" value={count(data.engagement.dormancy.dormant_90d)} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <MetricCard label="Tracked registered customers" value={count(data.engagement.dormancy.tracked_customers)} />
                    </Grid>
                  </Grid>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Month</TableCell>
                          <TableCell align="right">MAU</TableCell>
                          <TableCell align="right">Avg DAU</TableCell>
                          <TableCell align="right">DAU / MAU</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.engagement.monthly.map((row) => (
                          <TableRow key={row.month}>
                            <TableCell>{row.month}{row.is_partial_month ? ' (MTD)' : ''}</TableCell>
                            <TableCell align="right">{count(row.mau)}</TableCell>
                            <TableCell align="right">{count(row.avg_dau)}</TableCell>
                            <TableCell align="right">{row.dau_mau_ratio_percent.toFixed(1)}%</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </CardContent>
          </Card>

          <Alert severity="info" sx={{ mt: 2 }}>
            Order retention excludes canceled orders. The churn figure is an order-activity proxy,
            not contractual or subscription churn. Finance should reconcile GMV and commission
            before external circulation.
          </Alert>
        </>
      )}
    </Box>
  );
};

export default DashboardInvestorCohorts;
